import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid, Button,
  HStack, Icon, Badge, List, ListItem, ListIcon
} from "@chakra-ui/react";
import {
  FaSyncAlt, FaCheckCircle, FaArrowRight, FaPhone,
  FaDollarSign, FaChartLine, FaShieldAlt, FaHome
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FINANCE_PRIMARY = "#141C49";
const FINANCE_ACCENT = "#C2A878";
const NAVY = "#141C49";

const reasons = [
  {
    icon: FaDollarSign,
    title: "Lower Your Rate",
    description: "If your loan is more than 2 years old, there's a strong chance you're paying too much. We compare the market so you don't have to.",
    badge: "Most Common"
  },
  {
    icon: FaChartLine,
    title: "Access Your Equity",
    description: "Has your property grown in value? Refinancing lets you unlock that equity for renovations, an investment purchase, or anything else.",
    badge: "Growth"
  },
  {
    icon: FaSyncAlt,
    title: "Consolidate Debt",
    description: "Roll high-interest credit cards and personal loans into your mortgage. One manageable repayment at a much lower rate.",
    badge: "Simplify"
  },
  {
    icon: FaShieldAlt,
    title: "Better Loan Features",
    description: "Offset accounts, redraw facilities, flexible repayments — get features your current lender may not offer.",
    badge: "Upgrade"
  }
];

const steps = [
  { number: "01", title: "Get in touch", desc: "Tell us about your current loan and what you're hoping to achieve. No obligation, no credit impact." },
  { number: "02", title: "We review your situation", desc: "We look at your current rate, loan structure, and remaining term against what's available in the market today." },
  { number: "03", title: "Compare 40+ lenders", desc: "We search the full market — not just a handful of preferred partners — to find your best option." },
  { number: "04", title: "Handle the paperwork", desc: "We prepare and submit your application, liaise with your new lender, and manage the discharge from your old one." },
  { number: "05", title: "Settlement", desc: "Your new loan settles, the old one closes. You start saving from day one." },
  { number: "06", title: "Ongoing reviews", desc: "We check in annually to make sure your loan remains competitive. Rates change — we stay on it for you." }
];

const FinanceServicesRefinancing: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/refinancing"
        fallbackTitle="Refinancing - One Group Finance"
        fallbackDescription="Is your current home loan working for you? We compare 40+ lenders to find a better rate, access equity, or consolidate debt — with no upfront fees."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/finance-assets/asset 18.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge bg={FINANCE_ACCENT} color="white" fontSize="sm" px={4} py={1} borderRadius="full">
              Refinancing
            </Badge>
            <Heading size="2xl" fontWeight="bold" maxW="3xl">
              A Better Rate. A Better Life.
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Things in life change. Your home loan should keep up. We compare the market and help you switch — fast, simple, and free.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/finance/contact"
                size="lg"
                bg="white"
                color={NAVY}
                rightIcon={<FaArrowRight />}
                _hover={{ bg: FINANCE_ACCENT, color: "white" }}
                transition="all 0.2s"
              >
                Check My Rate
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                leftIcon={<FaPhone />}
                _hover={{ bg: "rgba(255,255,255,0.1)" }}
                transition="all 0.2s"
              >
                1300 536 159
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Is it time? */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={5}>
              <Text fontSize="sm" color={FINANCE_ACCENT} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
                You don't have to go it alone
              </Text>
              <Heading size="xl" color={NAVY}>
                Is It Time to Refinance?
              </Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                If your loan is more than 2 years old, there's a good chance you're paying more than you need to. Lenders offer their sharpest rates to attract new customers — not to reward loyal ones.
              </Text>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                We do the comparison work for you. We search 30+ banks and lenders, handle all the documentation and communication, and make sure the switch happens smoothly.
              </Text>
              <List spacing={3}>
                {[
                  "No obligation review of your current loan",
                  "No credit check for initial comparison",
                  "Free service — paid by the lender at settlement",
                  "Three-month pre-approval validity"
                ].map((item) => (
                  <ListItem key={item} display="flex" alignItems="center">
                    <ListIcon as={FaCheckCircle} color={FINANCE_ACCENT} />
                    <Text color="gray.700">{item}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>
            <Box
              h="400px"
              borderRadius="2xl"
              overflow="hidden"
              shadow="xl"
              backgroundImage="url('/assets/finance-assets/asset 7.jpeg')"
              backgroundSize="cover"
              backgroundPosition="center"
            />
          </SimpleGrid>
        </Container>
      </Box>

      {/* Reasons to Refinance */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">Why People Refinance</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Every situation is different. Here's what we help with most.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {reasons.map((reason, idx) => (
            <Box
              key={idx}
              p={8}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              _hover={{ shadow: "xl", borderColor: FINANCE_ACCENT, transform: "translateY(-4px)" }}
              transition="all 0.3s"
            >
              <HStack justify="space-between" mb={4}>
                <Icon as={reason.icon} boxSize={10} color={FINANCE_ACCENT} />
                <Badge bg={FINANCE_ACCENT} color="white" fontSize="xs">{reason.badge}</Badge>
              </HStack>
              <Heading size="md" mb={3} color="gray.800">{reason.title}</Heading>
              <Text color="gray.600" lineHeight="tall">{reason.description}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Process */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color={NAVY}>How Refinancing Works</Heading>
            <Text fontSize="lg" color="gray.600" maxW="xl">Sit back and enjoy the moment — we handle the hard part.</Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {steps.map((step) => (
              <Box
                key={step.number}
                p={8}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                position="relative"
                _hover={{ shadow: "lg", borderColor: FINANCE_ACCENT }}
                transition="all 0.3s"
              >
                <Text
                  fontSize="4xl"
                  fontWeight="900"
                  color={FINANCE_ACCENT}
                  opacity={0.3}
                  position="absolute"
                  top={4}
                  right={6}
                  lineHeight={1}
                >
                  {step.number}
                </Text>
                <Heading size="md" mb={3} color={NAVY}>{step.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="tall">{step.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl" textAlign="center">
          <Heading size="xl" mb={4}>Let's See How Much You Could Save</Heading>
          <Text fontSize="lg" mb={8} color="gray.300" maxW="xl" mx="auto">
            No obligation, no credit check. Just an honest comparison of your options.
          </Text>
          <HStack justify="center" spacing={4} flexWrap="wrap">
            <Button
              as={RouterLink}
              to="/finance/contact"
              size="lg"
              bg={FINANCE_ACCENT}
              color="white"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#a8905f" }}
              transition="all 0.2s"
            >
              Book a Free Review
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="white"
              color="white"
              leftIcon={<FaPhone />}
              _hover={{ bg: "rgba(255,255,255,0.1)" }}
              transition="all 0.2s"
            >
              1300 536 159
            </Button>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesRefinancing;
